import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import logo from '@/assets/icons/LucioLogoDark.svg';

interface AnimatedFullScreenLoaderProps {
  onHidden?: () => void;
  show: boolean;
  title?: string;
}

/**
 * A fullscreen loading spinner with smooth fade in/out animations.
 * Controls visibility with CSS transitions and manages component lifecycle
 * to ensure animations complete before mounting/unmounting. The component
 * handles proper timing for fade transitions and cleanup callbacks.
 *
 * @param title - The text to display below the spinner
 * @param show - Whether the loading spinner should be visible
 * @param onHidden - Optional callback fired when the component finishes hiding
 * @returns JSX element with fullscreen loading spinner or null when not rendered
 */
export const AnimatedFullScreenLoader = ({
  onHidden,
  show,
  title,
}: AnimatedFullScreenLoaderProps) => {
  const [isVisible, setIsVisible] = useState(show);
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      // Mount first, then fade in after a frame so the transition fires
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect
      setShouldRender(true);
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      // Start fade-out immediately, then unmount after the animation completes
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        onHidden?.();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show, onHidden]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={`bg-block-bg text-primary h-viewport fixed inset-0 z-50 flex flex-col items-center justify-center font-sans transition-opacity duration-300 ease-in-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="relative flex h-15 w-15 items-center justify-center">
        {/* Spinning Gradient Ring */}
        <motion.div
          animate={{ rotate: 360 }}
          className="absolute inset-0 rounded-full"
          style={{
            background: 'conic-gradient(from 0deg, #1C2E28 0%, #5A948100 100%)',
            maskImage: 'radial-gradient(transparent 62%, black 64%)',
            WebkitMaskImage: 'radial-gradient(transparent 62%, black 64%)',
          }}
          transition={{
            duration: 2.2,
            ease: 'linear',
            repeat: Infinity,
          }}
        />

        {/* Logo with subtle breathing effect */}
        <motion.img
          alt="Lucio Logo"
          animate={{ scale: [1, 0.75, 1] }}
          className="icon-themed z-10 h-7 w-7"
          src={logo}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
        />
      </div>
      {title ? (
        <motion.p
          animate={{ opacity: 1, y: 0 }}
          className="text-neutral-mid mt-8 text-sm font-medium"
          initial={{ opacity: 0, y: 5 }}
        >
          {title}
        </motion.p>
      ) : null}
    </div>
  );
};
